import { useForm, SubmitHandler } from "react-hook-form";
import { Image, Form, Button, Input, Alert } from "antd";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AxiosError } from "axios";

import { resetPassword } from "../Login/ducks/services";
import LoginTemplate from "../../templates/LoginTemplate";
import { EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";

interface FormValues {
  password: string;
  confirmPassword: string;
}

export default function UpdatePassword() {
  const { handleSubmit } = useForm<FormValues>({
    mode: "onChange",
  });

  const [loading, setLoading] = useState(false);
  const [linkExpired, setLinkExpired] = useState<boolean>(false);
  const [token, setToken] = useState<string>("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const navigate = useNavigate();
  const [form] = Form.useForm();

  useEffect(() => {
    const params = new URLSearchParams(window?.location?.search);
    const authId = params.get("auth");
    setToken(authId as string);
  }, []);

  const onSubmit: SubmitHandler<FormValues> = () => {
    setLoading(true);
    const payload: { newPassword: string; token: string } = {
      newPassword: newPassword,
      token: token,
    };

    resetPassword(payload)
      .then(() => {
        setLoading(false);
        setTimeout(() => {
          navigate("/login", { state: { id: 1, name: "update" } });
        }, 500);
      })
      .catch((e: unknown) => {
        setLoading(false);
        if (e instanceof AxiosError) {
          if (e?.response?.status === 401) {
            setLinkExpired(true);
          }
        }
      });
  };

  return (
    <LoginTemplate loading={loading}>
      <div className="loginForm update-password">
        <div className="loginLogo">
          <Image src="/images/login/logo.svg" alt="Logo" preview={false} />
        </div>

        <h1 style={{ marginBottom: "24px" }}>Create new password</h1>

        {linkExpired && (
          <div className="alertWrapper" style={{ marginBottom: "20px" }}>
            <Alert
              message="The password reset link has expired."
              type="error"
              showIcon
              closable
            />
          </div>
        )}
        <Form
          form={form}
          name="dependencies"
          autoComplete="off"
          style={{ maxWidth: 600 }}
          layout="vertical"
          onFinish={handleSubmit(onSubmit)}
        >
          <Form.Item
            label="New Password"
            name="password"
            rules={[{ required: true, message: "New password is required." }]}
          >
            <Input.Password
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              iconRender={(visible: boolean) =>
                visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
              }
              className="formControl"
            />
          </Form.Item>

          <Form.Item
            label="Confirm new password"
            name="confirmPassword"
            dependencies={["password"]}
            className="requiredInput mb_24"
            rules={[
              {
                required: true,
                message: "Confirm new password is required.",
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("password") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error("Passwords don't match"));
                },
              }),
            ]}
          >
            <Input.Password
              onChange={(e) => setConfirmNewPassword(e.target.value)}
              placeholder="Enter new password"
              iconRender={(visible: boolean) =>
                visible ? <EyeOutlined /> : <EyeInvisibleOutlined />
              }
              className="formControl"
            />
          </Form.Item>

          <Button
            htmlType="submit"
            type="primary"
            className="icon_dark_btn"
            // style={{ marginTop: "16px" }}
            disabled={newPassword?.length < 4 || confirmNewPassword?.length < 4}
          >
            Change password
          </Button>
        </Form>
      </div>
    </LoginTemplate>
  );
}
